import { settings } from '../core/storage.js';
import { t } from '../core/i18n.js';
import { notify } from '../core/notify.js';
import { escapeHtml } from '../core/utils.js';
import { allShells, shellById, addCustomShell, removeCustomShell, exportShell } from '../core/shells.js';

/* Selector de aspecto (shell): una tarjeta por shell con una vista previa en miniatura pintada con sus propios valores.
   Al tocar una se aplica al instante. Abajo, importar un shell (JSON pegado) y exportar el actual. */

let seq = 0;

/** Miniatura: fondo, una barra, tres carátulas (con la forma del shell) y un botón de acento, todo con las variables del shell. */
function preview(shell) {
    const v = shell.vars || {};
    const flags = shell.flags || [];
    const layout = shell.layout || {};
    const light = shell.base === 'light';
    const bg = v.bg || (light ? '#f4f4f6' : '#141414');
    const surface = v.surface || (light ? '#ffffff' : '#232323');
    const surface2 = v['surface-2'] || (light ? '#ececf0' : '#2e2e2e');
    const text = v.text || (light ? '#111' : '#fff');
    const text2 = v['text-2'] || (light ? '#555' : '#b3b3b3');
    const accent = v.accent || '#e50914';
    const radius = v.radius || '8px';
    const font = v.font || 'inherit';
    const border = flags.indexOf('flat') !== -1 || flags.indexOf('framed') !== -1 ? `border:1px solid ${v.border || 'rgba(128,128,128,.3)'};` : '';
    const posterStyle = `background:${surface2};${border}border-radius:${flags.indexOf('square') !== -1 ? radius : v['radius-sm'] || '4px'};aspect-ratio:${layout.cards === 'wide' ? '16 / 9' : flags.indexOf('square') !== -1 || layout.cards === 'square' || layout.cards === 'circle' ? '1' : '2 / 3'};${layout.cards === 'circle' ? 'border-radius:50%;' : ''}${flags.indexOf('framed') !== -1 ? `padding:3px;background-clip:content-box;background-color:${surface};` : ''}`;
    const titleStyle = `color:${text};font-family:${v['font-display'] || font};${flags.indexOf('caps') !== -1 ? 'text-transform:uppercase;letter-spacing:.05em;font-size:10px;' : 'font-size:11px;'}`;
    const side = layout.nav === 'side';
    const bottom = layout.nav === 'bottom';
    const dock = layout.nav === 'dock';
    const grid = layout.rows === 'grid';
    const glow = `radial-gradient(ellipse 70% 50% at 10% 0%,${v['accent-soft'] || 'rgba(229,9,20,.18)'},transparent 70%)`;
    const ground = layout.ground === 'glow' || layout.ground === 'stage' ? `background-image:${glow};`
        : layout.ground === 'lines' ? 'background-image:repeating-linear-gradient(0deg,rgba(128,128,128,.18) 0,rgba(128,128,128,.18) 1px,transparent 1px,transparent 9px);'
        : layout.ground === 'dots' ? 'background-image:radial-gradient(circle,rgba(128,128,128,.4) 1px,transparent 1.5px);background-size:8px 8px;'
        : layout.ground === 'grid' ? 'background-image:repeating-linear-gradient(0deg,rgba(128,128,128,.16) 0,rgba(128,128,128,.16) 1px,transparent 1px,transparent 12px),repeating-linear-gradient(90deg,rgba(128,128,128,.16) 0,rgba(128,128,128,.16) 1px,transparent 1px,transparent 12px);'
        : layout.ground === 'scan' ? `background-image:repeating-linear-gradient(0deg,rgba(128,128,128,.14) 0,rgba(128,128,128,.14) 1px,transparent 1px,transparent 3px),${glow};`
        : '';
    const bar = `<div class="shell-preview-bar" style="background:${surface};border-radius:${radius}"><span style="background:${accent}"></span><i style="background:${text2}"></i></div>`;
    return `<div class="shell-preview ${side ? 'is-side' : ''}" style="background-color:${bg};${ground}font-family:${font};border-radius:${v['radius-lg'] || '12px'}">
        ${side ? `<div class="shell-preview-rail" style="background:${surface};border-radius:${radius}"><span style="background:${accent}"></span><i style="background:${text2}"></i><i style="background:${text2}"></i></div>` : ''}
        <div class="shell-preview-main">
        ${side || bottom || dock ? '' : bar}
        <div class="shell-preview-title" style="${titleStyle}">${escapeHtml(t('shell.previewTitle'))}</div>
        <div class="shell-preview-row ${flags.indexOf('dense') !== -1 ? 'is-dense' : ''} ${grid ? 'is-grid' : ''}">${(grid ? [1, 2, 3, 4, 5, 6] : [1, 2, 3]).map(() => `<span style="${posterStyle}"></span>`).join('')}</div>
        <div class="shell-preview-btn" style="background:${accent};color:${v['on-accent'] || v['on-text'] || '#fff'};border-radius:${radius};font-family:${font}">${escapeHtml(t('common.play'))}</div>
        ${bottom ? bar : ''}
        ${dock ? `<div class="shell-preview-dock" style="background:${surface};border-radius:999px"><span style="background:${accent}"></span><i style="background:${text2}"></i><i style="background:${text2}"></i></div>` : ''}
        </div>
    </div>`;
}

function nameOf(shell) {
    return shell.custom ? shell.name : t('shell.' + shell.id);
}

function card(shell, active) {
    return `<button type="button" class="shell-card ${active ? 'is-active' : ''}" data-shell-id="${escapeHtml(shell.id)}">
        ${preview(shell)}
        <span class="shell-card-name">${escapeHtml(nameOf(shell))}${shell.custom ? ` <small>${t('shell.custom')}</small>` : ''}</span>
        <span class="shell-card-desc">${shell.custom ? '' : escapeHtml(t('shell.' + shell.id + '.desc'))}</span>
        ${active ? '<span class="mi shell-check">check_circle</span>' : ''}
    </button>`;
}

export function openShellPicker(onChange) {
    const app = window.app;
    const id = 'shell-' + (++seq) + '-' + Date.now().toString(36);
    const html = `<div class="popup catpick-popup admin-popup" id="${id}">
        <div class="view"><div class="page">
            <div class="navbar"><div class="navbar-bg"></div><div class="navbar-inner">
                <div class="title">${t('settings.shell')}</div>
                <div class="right"><a href="#" class="link popup-close" aria-label="${t('common.close')}"><span class="mi">close</span></a></div>
            </div></div>
            <div class="page-content">
                <p class="px small muted">${t('settings.shellSub')}</p>
                <div class="shell-grid" data-list data-nav-list></div>
                <div class="px shell-tools">
                    <button type="button" class="btn btn-block" data-import><span class="mi">file_download</span>${t('shell.import')}</button>
                    <button type="button" class="btn btn-block" data-export><span class="mi">content_copy</span>${t('shell.export')}</button>
                </div>
            </div>
        </div></div>
    </div>`;

    const popup = app.popup.create({
        content: html,
        on: {
            open(p) {
                const list = p.el.querySelector('[data-list]');
                const paint = () => {
                    const current = settings.get('shell') || 'cine';
                    list.innerHTML = allShells().map((s) => card(s, s.id === current)).join('');
                };
                paint();
                list.addEventListener('click', (e) => {
                    const btn = e.target.closest('[data-shell-id]');
                    if (!btn) return;
                    e.preventDefault();
                    const shellId = btn.getAttribute('data-shell-id');
                    const shell = shellById(shellId);
                    if (shell.custom && e.target.closest('.shell-card-name')) {
                        // Tocar el nombre de uno propio ofrece borrarlo.
                        notify.confirm(t('shell.removeConfirm', { name: shell.name }), t('shell.remove'), () => { removeCustomShell(shellId); if (onChange) onChange(); paint(); });
                        return;
                    }
                    if ((settings.get('shell') || 'cine') === shellId) return;
                    settings.set('shell', shellId);
                    if (onChange) onChange();
                    paint();
                    const again = list.querySelector(`[data-shell-id="${shellId}"]`);
                    if (again && window.IPTV.device.isTV) window.IPTV.tvnav.focus(again);
                });
                p.el.querySelector('[data-import]').addEventListener('click', () => {
                    app.dialog.create({
                        title: t('shell.import'),
                        content: `<div class="pw-dialog"><p class="pw-note">${t('shell.importHelp')}</p><div class="field"><textarea name="json" rows="7" spellcheck="false" placeholder='{"id":"mi-shell","name":"Mi shell","base":"dark","vars":{"bg":"#101820","accent":"#ff8800"},"flags":["square"]}'></textarea></div><div class="field-error" data-err hidden></div></div>`,
                        closeByBackdropClick: false,
                        buttons: [
                            { text: t('common.cancel') },
                            {
                                text: t('shell.importDo'), bold: true, close: false,
                                onClick: (dialog) => {
                                    const err = dialog.el.querySelector('[data-err]');
                                    try {
                                        const shell = addCustomShell(JSON.parse(dialog.el.querySelector('textarea').value));
                                        settings.set('shell', shell.id);
                                        if (onChange) onChange();
                                        paint();
                                        dialog.close();
                                        notify.toast(t('shell.imported', { name: shell.name }), { icon: 'check_circle' });
                                    } catch (e) {
                                        err.textContent = t(e && e.code === 'invalid_shell' ? 'shell.invalid' : 'shell.badJson');
                                        err.hidden = false;
                                    }
                                }
                            }
                        ]
                    }).open();
                });
                p.el.querySelector('[data-export]').addEventListener('click', async () => {
                    const json = exportShell(shellById(settings.get('shell') || 'cine'));
                    try {
                        await navigator.clipboard.writeText(json);
                        notify.toast(t('shell.exported'), { icon: 'content_copy' });
                    } catch (e) {
                        app.dialog.create({ title: t('shell.export'), content: `<div class="pw-dialog"><div class="field"><textarea rows="9" readonly onfocus="this.select()">${escapeHtml(json)}</textarea></div></div>`, buttons: [{ text: t('common.close'), bold: true }] }).open();
                    }
                });
            },
            opened(p) {
                if (window.IPTV.device.isTV) window.IPTV.tvnav.focusLayer(p.el);
            },
            closed(p) {
                p.destroy();
                const el = document.getElementById(id);
                if (el && el.parentNode) el.parentNode.removeChild(el);
            }
        }
    });
    popup.open();
    return popup;
}
